import { useEffect, useState } from 'react'
import { apiGet } from '@/lib/api'

export type UserRole = 'admin' | 'team' | 'founder' | 'fundraiser'

/** The current user's profile, from GET /api/v1/me (UserRead). */
export interface Me {
  id: string
  email: string
  full_name: string
  role: UserRole
  startup_id: string | null
  /** Operator flag (spec-21 D3): gates the all-users task views; role-independent. */
  is_operator: boolean
  /**
   * Fundraising archive flag (spec-30): the backend stamps its
   * FUNDRAISING_UI_ENABLED setting here. Off (default) hides the fundraising
   * landing card and makes every /fundraising route redirect to `/`.
   */
  fundraising_enabled?: boolean
}

/** Dutch labels for the user roles, shown in the sidebar profile block. */
export const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Beheerder',
  team: 'Team',
  founder: 'Founder',
  fundraiser: 'Fundraiser',
}

/**
 * Module-level cache for the current user's profile.
 *
 * The profile is identical for every consumer and only changes on sign-in/out,
 * so we fetch it once and share the result. Without this, each navigation
 * re-mounts PreinvestLayout, re-fires GET /api/v1/me, and flashes the
 * full-page "Bezig met laden…" loader — the shaky transition we're removing.
 *
 * Only a SUCCESSFUL fetch is memoised. We deliberately do NOT cache a failure:
 * a transient GET /api/v1/me error (a JWKS/HS256 hiccup, a backend cold-start,
 * or a momentary token gap) used to settle mePromise to null permanently,
 * so every later useMe() served that cached null as if the user had no
 * profile — blanking the whole app until a full page reload. Now a failed
 * fetch clears mePromise so the next mount re-fires the request, and each
 * fetch self-heals with a single bounded retry before surfacing an error.
 */
let meCache: Me | null = null
let mePromise: Promise<Me> | null = null

/** How many extra attempts a single fetchMe() makes after the first failure. */
const MAX_RETRIES = 1

function fetchMe(): Promise<Me> {
  // A successful fetch stays memoised: fetchMe() resolves on the next
  // microtask with no network call, so navigations never re-flash the loader.
  if (!mePromise) {
    mePromise = (async () => {
      let lastError: unknown
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
        try {
          const data = await apiGet<Me>('/api/v1/me')
          meCache = data
          return data
        } catch (err) {
          lastError = err
        }
      }
      // Every attempt failed. Clear the memoised promise (leaving meCache null)
      // so the NEXT useMe() mount re-fires the request instead of serving this
      // cached failure. Re-throw so callers can tell a failed fetch apart from
      // an authoritative "no profile" result.
      mePromise = null
      throw lastError
    })()
  }
  return mePromise
}

/** Clear the cached profile (call on sign-out so the next session re-fetches). */
export function resetMeCache(): void {
  meCache = null
  mePromise = null
}

/**
 * Fetch the current user's profile from GET /api/v1/me.
 *
 * UX only: the backend enforces RBAC with 403. Feeds both the role-gating and
 * the sidebar profile block (name + role + avatar initials). Backed by a
 * module-level cache so the profile resolves instantly after the first load and
 * navigating between pages never re-flashes the loader.
 *
 * The returned error flag distinguishes "loaded, genuinely no profile"
 * (me === null && !error) from "the fetch failed" (error === true); most
 * callers only read me/loading and can ignore it.
 */
export function useMe(): { me: Me | null; loading: boolean; error: boolean } {
  const [me, setMe] = useState<Me | null>(meCache)
  const [loading, setLoading] = useState(meCache === null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    // When the profile is already cached, fetchMe() resolves on the next
    // microtask without a network call, so navigations never re-flash the
    // loader; the first mount actually fetches (with a bounded retry).
    fetchMe().then(
      (data) => {
        if (!cancelled) {
          setMe(data)
          setError(false)
          setLoading(false)
        }
      },
      () => {
        // The fetch (incl. its retry) failed. Surface an error state instead of
        // a fake null profile, and stop loading. mePromise is already cleared,
        // so a later mount re-fires the request and the app can self-heal.
        if (!cancelled) {
          setMe(null)
          setError(true)
          setLoading(false)
        }
      },
    )
    return () => {
      cancelled = true
    }
  }, [])

  return { me, loading, error }
}
